import { deduplicateById } from '@/lib/utils';
import type { Employee, Identifiable } from '@/types/template';

/**
 * Resolves stored selections (full objects persisted on the shift) to the ids
 * of the currently-available options, so a `MultiSelect` can show them.
 *
 * Matches by **id first, then name** because the persisted objects can come
 * from a different id space than the freshly-fetched option lists (the provider
 * and backend mint ids independently). Selections that match neither are
 * dropped — same lenient behaviour as the legacy shift modal.
 */
export function reconcileSelectedIds(
  selected: Identifiable[],
  available: Identifiable[],
): string[] {
  const matched = selected
    .map((item) => available.find((option) => option.id === item.id || option.name === item.name))
    .filter((option): option is Identifiable => option !== undefined);
  return deduplicateById(matched).map((option) => option.id);
}

/**
 * Resolves a shift's stored fixed employee (the `specific` assignment's
 * `employee`) to an available employee id (id then name), or `''` when none is
 * set or it no longer matches a known employee.
 */
export function reconcileFixedEmployeeId(
  employee: Identifiable | undefined,
  employees: Employee[],
): string {
  if (!employee) return '';
  const match = employees.find(
    (candidate) => candidate.id === employee.id || candidate.name === employee.name,
  );
  return match?.id ?? '';
}

/**
 * Narrows the specific-employee picker's options to those eligible under the
 * chosen staff-group and tag filters, mirroring the legacy cascade:
 * - no filters → everyone;
 * - groups only → members of any selected group;
 * - tags only → holders of any selected tag;
 * - both → members of a selected group **and** holders of a selected tag.
 *
 * Group/tag membership matches by id or name to tolerate id-space differences
 * between the option lists and the denormalised employee snapshots.
 */
export function filterEligibleEmployees(
  employees: Employee[],
  selectedGroups: Identifiable[],
  selectedTags: Identifiable[],
): Employee[] {
  return employees.filter(
    (employee) =>
      matchesAny(employee.staffGroups, selectedGroups) && matchesAny(employee.tags, selectedTags),
  );
}

/** True when no filter is selected, or the candidate shares an id/name with one. */
function matchesAny(candidate: Identifiable[], selected: Identifiable[]): boolean {
  if (selected.length === 0) return true;
  return candidate.some((item) =>
    selected.some((option) => option.id === item.id || option.name === item.name),
  );
}
